/**
 * Mock 模块：get_activity_pendants
 * 默认正式已绑 · 仅阶段任务卡（对齐 state-stage.png 28000/30000）。
 * Preview `?state=` 切预热 / 未绑 / 每日 / 巅峰 / 回顾。
 * @see mockMd/get_activity_pendants.md
 */
import {
  storiesAvatar,
  storiesAvatarPeer,
} from '../components/RoomPendant/stories-images'
import {
  RelationType,
  type ActivityMemberV2,
  type HeartVoyageDailyBannerBlock,
  type HeartVoyagePeakBannerBlock,
  type HeartVoyagePendantPayload,
  type HeartVoyageStageBannerBlock,
} from '../types'

const MOCK_USER_AVATAR = storiesAvatar
const MOCK_PEER_AVATAR = storiesAvatarPeer

/** 稿面倒计时 24:59:59 */
const MOCK_COUNTDOWN_HMS = 24 * 3600 + 59 * 60 + 59

const mockMember = (
  id: number,
  name: string,
  avatar: string,
  gender: number
): ActivityMemberV2 => ({
  id,
  name,
  zy_id: String(id),
  avatar,
  gender,
})

const memberA = mockMember(10001, 'Alice', MOCK_USER_AVATAR, 2)
const memberB = mockMember(10002, 'Bob', MOCK_PEER_AVATAR, 1)

const emptyStageBanner: HeartVoyageStageBannerBlock = {
  member_a: null,
  member_b: null,
  rel_type: RelationType.Couple,
  ship_level: 0,
  affinity: 0,
  need_affinity: 0,
}

const stageBanner: HeartVoyageStageBannerBlock = {
  member_a: memberA,
  member_b: memberB,
  rel_type: RelationType.Couple,
  ship_level: 5,
  affinity: 28000,
  need_affinity: 30000,
}

const dailyBanner: HeartVoyageDailyBannerBlock = {
  member_a: memberA,
  member_b: memberB,
  rel_type: RelationType.Couple,
  round: 2,
  pair_affinity: 28000,
  threshold: 30000,
  round_countdown: MOCK_COUNTDOWN_HMS,
}

const peakBanner = (
  rank: number,
  scoreGap: number
): HeartVoyagePeakBannerBlock => ({
  member_a: memberA,
  member_b: memberB,
  rel_type: RelationType.Couple,
  segment: 1,
  group_id: 0,
  group_name: '海选赛',
  rank,
  score: 29999,
  score_gap: scoreGap,
  segment_countdown: MOCK_COUNTDOWN_HMS,
})

const PENDANT_URL =
  'https://example.com/h5/heart_voyage?other_info=%7B%22jump_to%22%3A%22home%22%2C%22from%22%3A%22room%22%7D'

const basePayload = (
  patch: Partial<HeartVoyagePendantPayload>
): HeartVoyagePendantPayload => ({
  activity_name: 'heart_voyage',
  activity_id: 12345,
  url: PENDANT_URL,
  weight: 100,
  stage: 2,
  has_relation: true,
  daily_can_join: false,
  stage_banner: emptyStageBanner,
  daily_banner: null,
  peak_banner: null,
  review_tops: [],
  segment_name: '',
  start_time_text: '',
  coin_reward: 0,
  coin_reward_icon: '',
  guide_text: '',
  self_member: null,
  has_empty_slot: false,
  ...patch,
})

/** 默认 VRT：阶段任务展开 */
export const GetActivityPendantsStageMock: HeartVoyagePendantPayload =
  basePayload({
    stage_banner: stageBanner,
  })

export const GetActivityPendantsPreheatMock: HeartVoyagePendantPayload =
  basePayload({
    stage: 1,
    has_relation: false,
    start_time_text: '8.1 00:00',
    coin_reward: 10000000,
  })

export const GetActivityPendantsUnboundMock: HeartVoyagePendantPayload =
  basePayload({
    has_relation: false,
    guide_text: 'Pusingan Kelayakan',
    self_member: memberA,
    has_empty_slot: true,
    coin_reward: 10000000,
  })

export const GetActivityPendantsDailyMock: HeartVoyagePendantPayload =
  basePayload({
    daily_can_join: true,
    daily_banner: dailyBanner,
  })

export const GetActivityPendantsPeakTop1Mock: HeartVoyagePendantPayload =
  basePayload({
    peak_banner: peakBanner(1, 29999),
  })

export const GetActivityPendantsPeakTop2Mock: HeartVoyagePendantPayload =
  basePayload({
    peak_banner: peakBanner(99, 29999),
  })

export const GetActivityPendantsReviewMock: HeartVoyagePendantPayload =
  basePayload({
    stage: 3,
    has_relation: false,
    review_tops: [
      {
        member_a: memberA,
        member_b: memberB,
        rel_type: RelationType.Couple,
        top_rank: 1,
      },
      {
        member_a: memberA,
        member_b: memberB,
        rel_type: RelationType.Couple,
        top_rank: 2,
      },
      {
        member_a: memberA,
        member_b: memberB,
        rel_type: RelationType.Couple,
        top_rank: 3,
      },
    ],
  })
